import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Users, CalendarDays, Ban, ListChecks, Wrench, ClipboardList, Pin, ArrowRightLeft, Trash2, Check, Star, DollarSign, StickyNote } from "lucide-react";
import { useAuth, useGet } from "@/context/AuthContext";
import { api, errMsg, saveRecord } from "@/lib/api";
import { money, dateLabel, time12 } from "@/lib/fmt";
import { Loading, ErrorBox, Section, Pill, priorityTone } from "@/components/app/Bits";
import { Attention } from "./Dashboard";
import { SetupProgress } from "@/components/app/Brief";
import { MorningBrief } from "@/components/app/Brief";

const Stat = ({ icon: Icon, value, label, to, testid }) => (
  <Link to={to} data-testid={testid} className="card-surface p-4 flex items-center gap-3 hover:bg-[#FBF9F5] min-h-[72px]">
    <Icon className="h-5 w-5 text-stone-700 shrink-0" />
    <div><div className="text-xl font-semibold num leading-none">{value}</div><div className="text-xs text-stone-500 mt-1">{label}</div></div>
  </Link>
);

export default function Today() {
  const { data, isLoading, error, refetch } = useGet("/today", { refetchInterval: 120000 });
  const { user } = useAuth();
  const qc = useQueryClient();
  if (isLoading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const d = data;
  const handoffItems = d.handoff ? ["business", "staffing", "kitchen", "bar", "guests", "facilities", "next_shift"].filter((k) => d.handoff[k]).length : 0;
  const done = async (t) => {
    const r = await saveRecord(`/tasks/${t.id}`, { status: "Completed" }, "patch").catch((e) => toast.error(errMsg(e)));
    if (r) { toast.success(r.queued ? "Saved offline" : "Task completed"); refetch(); qc.invalidateQueries({ queryKey: ["/tasks?limit=200"] }); }
  };
  const back = async (i) => { await api.patch(`/items86/${i.id}`, { active: false }).catch((e) => toast.error(errMsg(e))); toast.success(`${i.item} is back on`); refetch(); };
  return (
    <div data-testid="today-page">
      <div className="mb-6">
        <div className="label-eyebrow mb-2">{d.location}</div>
        <h1 className="text-3xl md:text-4xl font-display font-semibold">{dateLabel(d.date, { weekday: "long", month: "long", day: "numeric" })}</h1>
        <p className="text-stone-600 mt-1">Hi {user?.name?.split(" ")[0]} — here's tonight at a glance.</p>
      </div>
      <MorningBrief />
      <SetupProgress />
      <Section title="Tonight" testid="tonight-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {d.last_sale && <Stat icon={DollarSign} value={money(d.last_sale.net_sales)} label={d.last_sale.date === d.date ? "today's sales" : `sales ${dateLabel(d.last_sale.date, { weekday: "short" })}`} to="/app/sales" testid="stat-sales" />}
          <Stat icon={Users} value={d.reservations ?? d.expected_covers ?? "—"} label={d.reservations ? "covers booked" : "expected covers"} to="/app/log" testid="stat-covers" />
          <Stat icon={CalendarDays} value={d.beos_today.length} label="large parties" to="/app/beo" testid="stat-parties" />
          <Stat icon={Star} value={d.vips} label="VIP notes" to="/app/feed" testid="stat-vips" />
          <Stat icon={Ban} value={d.items86.length} label="items 86'd" to="/app/log" testid="stat-86" />
          <Stat icon={ListChecks} value={d.tasks.length} label="open tasks due" to="/app/tasks" testid="stat-tasks" />
          <Stat icon={Wrench} value={d.maintenance.length} label="maintenance issues" to="/app/log" testid="stat-maintenance" />
          <Stat icon={Trash2} value={money(d.waste_today)} label={`waste today (${d.waste_count})`} to="/app/waste" testid="stat-waste" />
          <Stat icon={ClipboardList} value={d.counts_due.length} label="counts due" to="/app/inventory?count=1" testid="stat-counts" />
        </div>
      </Section>
      {d.handoff && (
        <Link to="/app/handoff" data-testid="handoff-alert" className="flex items-center gap-3 rounded-xl bg-[#9E2A2B] text-white p-4 mb-8">
          <ArrowRightLeft className="h-5 w-5" />
          <div className="flex-1"><div className="font-semibold">Shift Handoff — {handoffItems} items need your attention</div><div className="text-xs text-white/80">From {d.handoff.created_by_name} · {d.handoff.shift}</div></div>
          <span className="text-sm underline">Review</span>
        </Link>
      )}
      {!!d.pins.length && (
        <Section title="Pinned" testid="pinned-section">
          <div className="space-y-2">{d.pins.map((p) => (
            <Link to="/app/feed" key={p.id} className="card-surface p-4 flex gap-3 border-l-4 border-l-[#9E2A2B]" data-testid={`pinned-${p.id}`}>
              <Pin className="h-4 w-4 mt-0.5 text-[#9E2A2B] shrink-0" />
              <div><div className="text-sm">{p.text}</div><div className="text-xs text-stone-500 mt-1">{p.created_by_name}{p.pin_until ? ` · pinned until ${dateLabel(p.pin_until)}` : ""}</div></div>
            </Link>))}</div>
        </Section>
      )}
      <Section title="Needs Attention"><Attention items={d.attention} /></Section>
      <Section title="Quick Actions" testid="quick-actions">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {[["waste", "Log waste", Trash2], ["item86", "86 an item", Ban], ["note", "Manager note", StickyNote], ["maintenance", "Maintenance", Wrench], ["guest", "Guest issue", Users], ["task", "New task", ListChecks]].map(([k, l, I]) => (
            <Link key={k} to={`?quick=${k}`} data-testid={`today-quick-${k}`} className="card-surface min-h-[80px] flex flex-col items-center justify-center gap-1.5 text-xs font-medium active:scale-95 transition-transform"><I className="h-5 w-5" />{l}</Link>))}
        </div>
      </Section>
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Tasks due" action={<Link to="/app/tasks" className="text-sm underline">All tasks</Link>}>
          {!d.tasks.length ? <div className="card-surface p-5 text-sm text-stone-500">No tasks due today.</div> : (
            <div className="card-surface divide-y">{d.tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-3" data-testid={`today-task-${t.id}`}>
                <button onClick={() => done(t)} data-testid={`complete-task-${t.id}`} className="h-11 w-11 rounded-full border-2 border-stone-300 flex items-center justify-center hover:border-stone-900 shrink-0" aria-label="Mark complete"><Check className="h-5 w-5 text-stone-300" /></button>
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{t.title}</div><div className="text-xs text-stone-500">{t.due_date < d.date ? <span className="text-rose-700 font-medium">Overdue · </span> : ""}{t.assigned_name || "Unassigned"}</div></div>
                <Pill tone={priorityTone(t.priority)}>{t.priority}</Pill>
              </div>))}</div>
          )}
        </Section>
        <Section title="Checklists" action={<Link to="/app/checklists" className="text-sm underline">Open</Link>}>
          <div className="card-surface divide-y">{d.checklists.map((c) => (
            <Link to={`/app/checklists?i=${c.id}`} key={c.id} className="flex items-center gap-3 p-3 hover:bg-[#FBF9F5]" data-testid={`today-checklist-${c.id}`}>
              <div className="flex-1 text-sm font-medium">{c.name}</div>
              <div className="w-24 h-2 rounded-full bg-[#F3EFE6] overflow-hidden"><div className="h-full bg-stone-900" style={{ width: `${c.total ? (c.done / c.total) * 100 : 0}%` }} /></div>
              <span className="text-xs text-stone-500 w-10 text-right num">{c.done}/{c.total}</span>
            </Link>))}</div>
        </Section>
        <Section title="86'd right now">
          {!d.items86.length ? <div className="card-surface p-5 text-sm text-stone-500">Nothing 86'd. Use Quick Add → 86'd Item.</div> : (
            <div className="flex flex-wrap gap-2">{d.items86.map((i) => (
              <button key={i.id} onClick={() => back(i)} data-testid={`item86-${i.id}`} className="min-h-[44px] px-4 rounded-full bg-stone-900 text-white text-sm flex items-center gap-2">
                <Ban className="h-4 w-4" /> {i.item} <span className="text-xs text-stone-400">· tap when back</span>
              </button>))}</div>
          )}
        </Section>
        <Section title="Upcoming events">
          {!d.beos_today.length && !d.upcoming.length ? <div className="card-surface p-5 text-sm text-stone-500">No large parties in the next 7 days.</div> : (
            <div className="card-surface divide-y">{[...d.beos_today, ...d.upcoming].map((b) => (
              <Link to={`/app/beo/${b.id}`} key={b.id} className="flex items-center gap-3 p-3 hover:bg-[#FBF9F5]">
                <div className="w-14 text-center"><div className="text-[11px] uppercase text-stone-500">{dateLabel(b.event_date, { weekday: "short" })}</div><div className="font-semibold">{dateLabel(b.event_date, { day: "numeric" })}</div></div>
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{b.event_name}</div><div className="text-xs text-stone-500">{b.guest_count} guests · {time12(b.start_time) || "—"} · {b.room || ""}</div></div>
                <Pill tone={b.deposit_status === "Paid" ? "green" : "amber"}>{b.status}</Pill>
              </Link>))}</div>
          )}
        </Section>
        {!!d.maintenance.length && (
          <Section title="Maintenance">
            <div className="card-surface divide-y">{d.maintenance.map((m) => (
              <div key={m.id} className="p-3 flex items-center gap-3"><Wrench className="h-4 w-4 text-stone-500" /><div className="flex-1 text-sm">{m.issue}<div className="text-xs text-stone-500">{m.area} · {m.status}</div></div><Pill tone={priorityTone(m.priority)}>{m.priority}</Pill></div>))}</div>
          </Section>
        )}
        {d.staffing_notes && <Section title="Staffing"><div className="card-surface p-4 text-sm">{d.staffing_notes}</div></Section>}
      </div>
    </div>
  );
}
